// @ts-check

/**
 * <video-faq-reels>
 *
 * Click-to-play vertical video reels in a Swiper slider. Each card shows its
 * poster + play button, paused. Clicking a card plays it (with sound) and
 * pauses any other reel. The speaker icon toggles mute.
 *
 * The prev / next arrows step one reel at a time, with one refinement: when the
 * track doesn't divide evenly, Swiper leaves a tiny final snap a few px from the
 * end (so the last reel needs an awkward extra "stuck" click to come fully into
 * view). We fold that micro-step into the end, so reaching the last reel is a
 * single, meaningful click. Touch dragging keeps Swiper's native behaviour.
 *
 * Swiper 11 is loaded globally (window.Swiper) from layout/theme.liquid.
 */
class VideoFaqReels extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLVideoElement[]} */
    this.videos = [];
    /** @type {any} */
    this.swiper = null;
    /** @type {HTMLButtonElement|null} */
    this.prevBtn = null;
    /** @type {HTMLButtonElement|null} */
    this.nextBtn = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.videos = Array.prototype.slice.call(this.querySelectorAll('[data-vfaq-video]'));

    this.setupCards();
    this.ensureSwiper((Swiper) => this.initSlider(Swiper));
  }

  disconnectedCallback() {
    if (this.swiper && typeof this.swiper.destroy === 'function') {
      try { this.swiper.destroy(true, true); } catch (_) { /* noop */ }
    }
    this.swiper = null;
  }

  /**
   * Run the callback once the Swiper library is available. The bundle loads
   * synchronously in <head>, but we poll a few frames just in case.
   * @param {(Swiper: any) => void} callback
   * @param {number} [attempts]
   */
  ensureSwiper(callback, attempts = 30) {
    const Swiper = /** @type {any} */ (window).Swiper;
    if (Swiper) { callback(Swiper); return; }
    if (attempts <= 0) return;
    requestAnimationFrame(() => this.ensureSwiper(callback, attempts - 1));
  }

  /**
   * @param {any} Swiper
   */
  initSlider(Swiper) {
    const el = this.querySelector('[data-vfaq-track]');
    if (!el) return;

    this.prevBtn = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-vfaq-prev]'));
    this.nextBtn = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-vfaq-next]'));

    // We drive the arrows ourselves (see goTo / cleanSnapGrid), so Swiper's
    // built-in navigation is intentionally left off.
    this.swiper = new Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      watchOverflow: true,
      breakpoints: {
        768: { spaceBetween: 12 },
      },
    });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(1));

    this.swiper.on('setTranslate transitionEnd resize update observerUpdate', () => this.updateArrows());
    this.updateArrows();
  }

  /**
   * Snap positions (positive px) with the redundant near-end micro-snap folded
   * into the end, so a single click reveals the last reel fully.
   * @returns {number[]}
   */
  cleanSnapGrid() {
    const sw = this.swiper;
    if (!sw || !sw.snapGrid) return [0];
    const snaps = sw.snapGrid.map((/** @type {number} */ n) => Math.abs(n));
    if (snaps.length >= 3) {
      const step = snaps[1] - snaps[0];
      const lastGap = snaps[snaps.length - 1] - snaps[snaps.length - 2];
      if (step > 0 && lastGap < step * 0.6) snaps.splice(snaps.length - 2, 1);
    }
    return snaps;
  }

  /**
   * Move one snap step in the given direction (1 = next, -1 = prev).
   * @param {number} dir
   */
  goTo(dir) {
    const sw = this.swiper;
    if (!sw) return;
    const snaps = this.cleanSnapGrid();
    const cur = -sw.translate;
    let target = null;
    if (dir > 0) {
      target = snaps.find((s) => s > cur + 1);
    } else {
      snaps.forEach((s) => { if (s < cur - 1) target = s; });
    }
    if (target != null) sw.translateTo(-target, sw.params.speed);
  }

  updateArrows() {
    const sw = this.swiper;
    if (!sw) return;
    const snaps = this.cleanSnapGrid();
    const first = snaps[0] ?? 0;
    const last = snaps[snaps.length - 1] ?? 0;
    const cur = -sw.translate;
    this.setArrowDisabled(this.prevBtn, cur <= first + 1);
    this.setArrowDisabled(this.nextBtn, cur >= last - 1);
  }

  /**
   * @param {HTMLButtonElement|null} btn
   * @param {boolean} disabled
   */
  setArrowDisabled(btn, disabled) {
    if (!btn) return;
    btn.disabled = disabled;
    btn.classList.toggle('swiper-button-disabled', disabled);
  }

  /**
   * Pause every reel except the one passed in.
   * @param {HTMLVideoElement} except
   */
  pauseOthers(except) {
    this.videos.forEach((video) => {
      if (video !== except) video.pause();
    });
  }

  setupCards() {
    this.querySelectorAll('[data-vfaq-card]').forEach((card) => {
      const video = /** @type {HTMLVideoElement|null} */ (card.querySelector('[data-vfaq-video]'));
      const mute = card.querySelector('[data-vfaq-mute]');
      if (!video) return;

      card.addEventListener('click', () => {
        if (video.paused) {
          this.pauseOthers(video);
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', () => card.classList.add('is-playing'));
      video.addEventListener('pause', () => card.classList.remove('is-playing'));
      video.addEventListener('ended', () => {
        card.classList.remove('is-playing');
        video.currentTime = 0;
      });

      if (mute) {
        mute.addEventListener('click', (ev) => {
          ev.stopPropagation();
          video.muted = !video.muted;
          mute.setAttribute('aria-pressed', String(!video.muted));
        });
      }
    });
  }
}

if (!window.customElements.get('video-faq-reels')) {
  window.customElements.define('video-faq-reels', VideoFaqReels);
}
